import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type PensionFormType, getPensionFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/pensionFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ContractProposalBaseInvestmentToolType,
  CreateContractProposalPensionSavingBodyInvestmentToolType,
  PatchContractProposalPensionSavingBodyInvestmentToolType,
  PensionSavingDetailFundType,
  PensionSavingDetailPensionSavingStrategy,
} from "Infrastructure/ApiZfp/Schema";
import type {
  ContractProposalBaseSolutionHorizonType,
  CreateContractProposalPensionSavingBody,
  PatchContractProposalPensionSavingBody,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailPensionSaving } from "Infrastructure/ApiZfp/Schema/contractProposalDetailPensionSaving";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { format } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";

type Props = {
  readonly edit?: boolean;
  readonly partner?: string;
  readonly proposedSolutionId?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly initialData?: ContractProposalDetailPensionSaving | null;
};

export const PensionForm = forwardRef<{}, Props>(({ partner, edit, proposedSolutionId, horizonType, onSuccess, initialData }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const partnerRef = useRef(partner);

  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const clientOptions: Option[] = (clientsData?.clients ?? [])
    .filter((client) => !isPlannedChild(client))
    .map((client) => ({
      value: client.guid,
      name: `${client.firstName} ${client.surname} (${client.birthDate ? format(new Date(client.birthDate), "dd.MM.yyyy") : ""})`,
    }));
  const fundOptions: Option[] = (Object.keys(PensionSavingDetailFundType) as Array<keyof typeof PensionSavingDetailFundType>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions[value]),
    }),
  );
  const strategyOptions: Option[] = (
    Object.keys(PensionSavingDetailPensionSavingStrategy) as Array<keyof typeof PensionSavingDetailPensionSavingStrategy>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions[value]),
  }));

  const methods = useForm<PensionFormType>({
    resolver: yupResolver(getPensionFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: initialData.investmentToolType,
            partner: partner ?? "",
            clientId: initialData.client?.guid ?? "",
            fund: initialData.pensionSavingDetail?.fundType as PensionSavingDetailFundType,
            accountState: initialData.oneTimeAmount?.value ?? 0,
            clientDeposit: initialData.regularAmount?.value ?? 0,
            employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
            appreciationRate: initialData.appreciationRate ?? 0,
            strategy: initialData.pensionSavingDetail?.pensionSavingStrategy as unknown as string,
            stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
            managementFee: initialData.managementFee ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            note: initialData.note ?? "",
            horizon: initialData.currentSolutionHorizon ?? 0,
          }
        : {
            investmentToolType: ContractProposalBaseInvestmentToolType.pension_saving,
            partner: partner ?? "",
          },
  });

  const { control, handleSubmit, setValue, reset, trigger } = methods;

  useEffect(() => {
    if (edit && initialData) {
      reset({
        investmentToolType: initialData.investmentToolType,
        partner: partnerRef.current ?? "",
        clientId: initialData.client?.guid ?? "",
        fund: initialData.pensionSavingDetail?.fundType as PensionSavingDetailFundType,
        accountState: initialData.oneTimeAmount?.value ?? 0,
        clientDeposit: initialData.regularAmount?.value ?? 0,
        employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
        appreciationRate: initialData.appreciationRate ?? 0,
        strategy: initialData.pensionSavingDetail?.pensionSavingStrategy as unknown as string,
        stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
        managementFee: initialData.managementFee ?? 0,
        equities: initialData.assetAllocation?.equities ?? 0,
        bonds: initialData.assetAllocation?.bonds ?? 0,
        commodities: initialData.assetAllocation?.commodities ?? 0,
        realEstate: initialData.assetAllocation?.realEstate ?? 0,
        cash: initialData.assetAllocation?.cash ?? 0,
        other: initialData.assetAllocation?.others ?? 0,
        note: initialData.note ?? "",
        horizon: initialData.currentSolutionHorizon ?? 0,
      });
    }
  }, [edit, initialData, reset]);

  useEffect(() => {
    setValue("partner", partner ?? "", { shouldValidate: true });
  }, [partner, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (edit) {
        handleSubmit(onEdit)();
      } else {
        handleSubmit(onCreate)();
      }
    },
    triggerValidation: () => {
      trigger();
    },
  }));

  const onCreate = (data: PensionFormType) => {
    if (!horizonType) return;

    const parsedPartnerId = Number(data.partner);
    const payload: CreateContractProposalPensionSavingBody = {
      investmentToolType: CreateContractProposalPensionSavingBodyInvestmentToolType.pension_saving,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      clientGuid: data.clientId || undefined,
      oneTimeAmount: { value: data.accountState ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.clientDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      pensionSavingDetail: {
        pensionSavingStrategy: data.strategy as PensionSavingDetailPensionSavingStrategy,
        fundType: data.fund as PensionSavingDetailFundType,
        employerContribution: { value: data.employerDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
        stateContribution: { value: data.stateContribution ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      },
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  const onEdit = (data: PensionFormType) => {
    if (!proposedSolutionId) return;

    const parsedPartnerId = Number(data.partner);
    const payload: PatchContractProposalPensionSavingBody = {
      investmentToolType: PatchContractProposalPensionSavingBodyInvestmentToolType.pension_saving,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      note: data.note ?? "",
      partnerId: Number.isFinite(parsedPartnerId) ? parsedPartnerId : 0,
      clientGuid: data.clientId || undefined,
      oneTimeAmount: { value: data.accountState ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.clientDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      pensionSavingDetail: {
        pensionSavingStrategy: data.strategy as PensionSavingDetailPensionSavingStrategy,
        fundType: data.fund as PensionSavingDetailFundType,
        employerContribution: { value: data.employerDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
        stateContribution: { value: data.stateContribution ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      },
    };

    updateMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          onSuccess?.();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormSelect
                  control={control}
                  name="clientId"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ClientId)}
                  required
                  options={toSelectOptions(clientOptions)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormSelect
                  control={control}
                  name="fund"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions.Title)}
                  required
                  options={toSelectOptions(fundOptions)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="accountState"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.CurrentValue)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="horizon"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                  required
                  scale={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.Years)}
                  fullWidth
                />
              </Grid>

              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="clientDeposit"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="employerDeposit"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EmployerDeposit)}
                  scale={2}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="appreciationRate"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                  required
                  scale={2}
                  thousandsSeparator=" "
                  unit="%"
                  fullWidth
                />
              </Grid>

              <Grid size={4}>
                <FormSelect
                  control={control}
                  name="strategy"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions.Title)}
                  required
                  options={toSelectOptions(strategyOptions)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="stateContribution"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.StateContribution)}
                  scale={2}
                  thousandsSeparator=" "
                  unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="managementFee"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFee)}
                  scale={2}
                  thousandsSeparator=" "
                  unit="%"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <CharacteristicsAccordion<PensionFormType>
              key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
              defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
            />
          </Box>

          <Divider />

          <Box>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
